/**
 * Typography Utilities for Relative Font Sizing
 *
 * This module provides utilities for creating scalable font sizes
 * based on a configurable base size. All font sizes are derived
 * from the base using ratios, enabling easy accessibility scaling.
 *
 * @example
 * // Default sizing (base = 14px)
 * const sizes = createFontSizes(); // tn=10px, xs=12px, sm=14px, xl=18px
 *
 * // Large accessibility mode (base = 16px)
 * const largeSizes = createFontSizes(16); // tn=11.4px, xs=13.7px, sm=16px, xl=20.6px
 */

/**
 * Default base font size in pixels.
 * This matches the current production value.
 */
export const DEFAULT_BASE_FONT_SIZE = 14;

/**
 * Font size ratios relative to base.
 * These ratios are calculated to match the current pixel values
 * when base = 14px:
 * - tn: 10px → 10/14 = 0.714 (tiny)
 * - xs: 12px → 12/14 = 0.857
 * - sm: 14px → 14/14 = 1.0
 * - base: 14px → 14/14 = 1.0
 * - md: 14.5px → 14.5/14 = 1.036
 * - lg: 15px → 15/14 = 1.071
 * - xl: 18px → 18/14 = 1.286
 */
export const FONT_SIZE_RATIOS = {
  tn: 10 / 14,      // 0.714
  xs: 12 / 14,      // 0.857
  sm: 14 / 14,      // 1.0
  base: 14 / 14,    // 1.0
  md: 14.5 / 14,    // 1.036
  lg: 15 / 14,      // 1.071
  xl: 18 / 14,      // 1.286
} as const;

/**
 * Font size preset names for common accessibility configurations.
 */
export const FONT_SCALE_PRESETS = {
  compact: 12,
  normal: 14,
  comfortable: 16,
  large: 18,
} as const;

export type FontScalePreset = keyof typeof FONT_SCALE_PRESETS;

/**
 * Typography configuration options.
 */
export interface TypographyConfig {
  /**
   * Base font size in pixels. All other sizes are derived from this value.
   * @default 14
   */
  baseFontSize?: number;
}

/**
 * Generated font sizes type.
 */
export interface FontSizes {
  tn: string;
  xs: string;
  sm: string;
  base: string;
  md: string;
  lg: string;
  xl: string;
  icons: string;
}

/**
 * Rounds a number to one decimal place for consistent pixel values.
 */
function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Creates font sizes based on a configurable base size.
 * All sizes are calculated using ratios relative to the base.
 *
 * @param baseFontSize - The base font size in pixels (default: 14)
 * @returns Font sizes object with pixel values as strings
 *
 * @example
 * // Default (14px base)
 * createFontSizes();
 * // { tn: '10px', xs: '12px', sm: '14px', base: '14px', md: '14.5px', lg: '15px', xl: '18px', icons: '24px' }
 *
 * @example
 * // Large accessibility mode (16px base)
 * createFontSizes(16);
 * // { tn: '11.4px', xs: '13.7px', sm: '16px', base: '16px', md: '16.6px', lg: '17.1px', xl: '20.6px', icons: '24px' }
 */
export function createFontSizes(baseFontSize: number = DEFAULT_BASE_FONT_SIZE): FontSizes {
  const tn = roundToOneDecimal(baseFontSize * FONT_SIZE_RATIOS.tn);
  const xs = roundToOneDecimal(baseFontSize * FONT_SIZE_RATIOS.xs);
  const sm = baseFontSize;
  const base = baseFontSize;
  const md = roundToOneDecimal(baseFontSize * FONT_SIZE_RATIOS.md);
  const lg = roundToOneDecimal(baseFontSize * FONT_SIZE_RATIOS.lg);
  const xl = roundToOneDecimal(baseFontSize * FONT_SIZE_RATIOS.xl);

  return {
    tn: `${tn}px`,
    xs: `${xs}px`,
    sm: `${sm}px`,
    base: `${base}px`,
    md: `${md}px`,
    lg: `${lg}px`,
    xl: `${xl}px`,
    icons: '24px', // Icons maintain fixed size for consistency
  };
}

/**
 * Creates font sizes from a preset name.
 *
 * @param preset - The preset name ('compact', 'normal', 'comfortable', 'large')
 * @returns Font sizes object with pixel values as strings
 *
 * @example
 * createFontSizesFromPreset('large');
 * // { tn: '12.9px', xs: '15.4px', sm: '18px', base: '18px', md: '18.6px', lg: '19.3px', xl: '23.1px', icons: '24px' }
 */
export function createFontSizesFromPreset(preset: FontScalePreset): FontSizes {
  return createFontSizes(FONT_SCALE_PRESETS[preset]);
}
